// Linking a project to an event: the search picker (shared with the new-project
// form), the event plate in the project header, and the prompt that offers to move dates.

import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useToast } from '../components/Toast/ToastContext';
import { useConfirm } from '../components/ConfirmModal/ConfirmContext';
import { Alert } from '../components/ui/Alert';
import Appear from '../components/ui/Appear';
import { Button } from '../components/ui/Button';
import { EmptyState } from '../components/ui/EmptyState';
import Reveal from '../components/ui/Reveal';
import SearchField from '../components/ui/SearchField';
import Skeleton from '../components/ui/Skeleton';
import Tooltip from '../components/ui/Tooltip';
import { IconArrowUpRight, IconPlus, IconSpark } from '../components/ui/icons';
import { CONTROL_MOTION, FOCUS, STATE } from '../components/ui/motion';
import { linkEvent, searchEvents, shiftDeliverables, unlinkEvent } from './sparkApi';
import type { DateOffer, EventHit, LinkedEvent } from './sparkApi';
import { businessToday, parseDate } from '../utils/date-utils';
import { dayText, errorMessage } from './sparkUi';

/** A search box over events. With nothing typed it lists the most recently created. */
export const EventLinkPicker: React.FC<{
  onPick: (event: EventHit) => void;
  onCancel?: () => void;
  autoFocus?: boolean;
}> = ({ onPick, onCancel, autoFocus }) => {
  const [q, setQ] = useState('');
  const [hits, setHits] = useState<EventHit[] | null>(null);
  const [fresh, setFresh] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const seq = useRef(0);

  useEffect(() => {
    const mine = ++seq.current;
    setError(null);
    setFresh(false);
    const t = setTimeout(() => {
      searchEvents(q.trim())
        .then((r) => { if (mine === seq.current) { setHits(r.events); setFresh(true); } })
        .catch((e) => { if (mine === seq.current) { setHits([]); setFresh(true); setError(errorMessage(e, 'Could not search events.')); } });
    }, q ? 250 : 0);
    return () => clearTimeout(t);
  }, [q]);

  return (
    <div className="mt-2">
      <SearchField
        id="spark-event-search"
        label="Search events by name"
        autoFocus={autoFocus}
        value={q}
        onChange={setQ}
        onKeyDown={(e) => {
          if (e.key === 'Enter') { e.preventDefault(); if (fresh && hits && hits[0]) onPick(hits[0]); }
          if (e.key === 'Escape' && onCancel) { e.stopPropagation(); onCancel(); }
        }}
        placeholder="Search events by name"
      />
      <p className="m-0 mt-1.5 px-1 text-xs text-on-surface-variant">
        {q.trim() ? 'Events not linked to a project yet.' : 'Recently created events not linked to a project yet.'}
      </p>
      <Reveal show={!!error}>
        <Alert tone="error" variant="text" className="mt-1 px-1">{error}</Alert>
      </Reveal>
      <ul className="m-0 mt-1 max-h-64 list-none overflow-y-auto p-0">
        {hits === null && [0, 1, 2].map((i) => <li key={i} className="py-0.5"><Skeleton className="h-9" /></li>)}
        {hits && hits.length === 0 && !error && (
          <li className="px-1 py-2">
            <EmptyState variant="line">
              {q.trim() ? `No unlinked event matches "${q.trim()}". Try part of the name.` : 'No unlinked events to show. Book one on the Events page, then link it here.'}
            </EmptyState>
          </li>
        )}
        {hits && hits.map((h) => (
          <li key={h.id} className="motion-safe:animate-rowLand">
            <button
              type="button"
              onClick={() => onPick(h)}
              className={`flex min-h-9 w-full items-baseline gap-3 rounded-control px-3 py-2 text-left ${STATE} ${CONTROL_MOTION} ${FOCUS}`}
            >
              <span className="min-w-0 flex-1 truncate text-sm font-medium text-on-surface">{h.name}</span>
              <span className="flex-none text-xs tabular-nums text-on-surface-variant">{dayText(h.event_date, 'MMM D, YYYY')}</span>
              {h.location && <span className="hidden max-w-[10rem] flex-none truncate text-xs text-on-surface-variant sm:inline">{h.location}</span>}
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
};

/** "The event is X, this project said Y. Move the N open deliverables D days later?" */
export const DateOfferPrompt: React.FC<{
  productionId: string;
  offer: DateOffer;
  studio: boolean;
  onDone: (shifted: boolean) => void;
}> = ({ productionId, offer, studio, onDone }) => {
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const days = Math.abs(offer.days);
  const dir = offer.days > 0 ? 'later' : 'earlier';

  const move = async () => {
    setBusy(true);
    try {
      const r = await shiftDeliverables(productionId, offer.days);
      toast.success(
        offer.open_dated > 0
          ? `Moved ${r.shifted} deliverable${r.shifted === 1 ? '' : 's'} ${days} day${days === 1 ? '' : 's'} ${dir}.`
          : `Moved the preview date ${days} day${days === 1 ? '' : 's'} ${dir}.`,
      );
      onDone(true);
    } catch (e) {
      toast.error(errorMessage(e, 'Could not move the dates. Try again.'));
      setBusy(false);
    }
  };

  const dayWord = `${days} day${days === 1 ? '' : 's'} ${dir}`;
  const question = offer.open_dated > 0
    ? `Move the ${offer.open_dated} open deliverable${offer.open_dated === 1 ? '' : 's'} ${dayWord}?`
    : `Move the preview date ${dayWord}?`;

  return (
    <div role="group" aria-label="Move deliverable dates" className="mt-3 rounded-control bg-primary/10 p-3 text-sm text-on-surface">
      {studio ? (
        <>
          <p className="m-0">The event is {dayText(offer.to)}, this project said {dayText(offer.from)}. {question}</p>
          <div className="mt-2.5 flex flex-wrap gap-2">
            <Tooltip label={`Shift them ${dayWord}`}>
              <Button variant="filled" size="sm" disabled={busy} onClick={move}>Move them</Button>
            </Tooltip>
            <Tooltip label="Leave every date where it is">
              <Button size="sm" disabled={busy} onClick={() => onDone(false)}>Keep the dates</Button>
            </Tooltip>
          </div>
        </>
      ) : (
        <>
          <p className="m-0">The show date now follows the event. Someone in the Studio can move the deliverables.</p>
          <div className="mt-2.5">
            <Button size="sm" onClick={() => onDone(false)}>Got it</Button>
          </div>
        </>
      )}
    </div>
  );
};

/** Days from today to a show date, and whether it is close enough to light up. */
const showWhen = (ymd: string): { text: string; soon: boolean; past: boolean } => {
  const n = parseDate(ymd).diff(businessToday(), 'day') as number;
  if (n === 0) return { text: 'today', soon: true, past: false };
  if (n === 1) return { text: 'tomorrow', soon: true, past: false };
  if (n > 1) return { text: `in ${n} days`, soon: n <= 7, past: false };
  return { text: n === -1 ? 'yesterday' : `${-n} days ago`, soon: false, past: true };
};

const COUNTRY = /^(usa?|u\.s\.a?\.?|united states( of america)?|canada|mexico|uk|united kingdom)$/i;
// "IL 62701", "IL", "Ontario M5V 2T6": the region segment a city sits just before.
const REGION = /^([A-Z]{2}|[A-Za-z .]+\s+[A-Z0-9][A-Z0-9 -]{2,})$/;

/**
 * "Venue, City" out of a full comma address. Few segments come back whole; the full address
 * stays available in the tooltip.
 */
export const venueCity = (location: string): string => {
  const segs = location.split(',').map((x) => x.trim()).filter(Boolean);
  if (segs.length > 1 && COUNTRY.test(segs[segs.length - 1])) segs.pop();
  if (segs.length <= 2) return segs.join(', ');
  const venue = segs[0];
  let city = segs[segs.length - 1];
  for (let i = segs.length - 1; i > 1; i -= 1) {
    if (REGION.test(segs[i]) && !/^\d/.test(segs[i - 1])) { city = segs[i - 1]; break; }
  }
  if (/^\d/.test(city)) city = segs[segs.length - 2];
  return city === venue ? venue : `${venue}, ${city}`;
};

/** The short place, with the whole address on hover or focus when it was shortened. */
const Place: React.FC<{ location: string; className?: string }> = ({ location, className = '' }) => {
  const short = venueCity(location);
  if (short === location.trim()) return <span className={className}>{location}</span>;
  return (
    <Tooltip label={location}>
      {/* relative: the sr-only address is absolute and would escape the truncating box, widening a phone page. */}
      <span tabIndex={0} className={`relative rounded-inner ${FOCUS} ${className}`}>
        <span aria-hidden="true">{short}</span>
        <span className="sr-only">{location}</span>
      </span>
    </Tooltip>
  );
};

/** One more linked event (a tour stop), under the lead one. */
const MoreEventRow: React.FC<{ ev: LinkedEvent; onUnlink: () => void }> = ({ ev, onUnlink }) => (
  <li className="flex min-w-0 items-center gap-3 rounded-control bg-surface-container py-1.5 pl-3 pr-1.5">
    <Link to={`/events/${ev.id}`} className={`min-w-0 truncate rounded-inner text-sm font-medium text-on-surface underline-offset-2 hover:underline ${FOCUS}`}>
      {ev.name}
    </Link>
    <span className="flex-none text-xs tabular-nums text-on-surface-variant">{dayText(ev.event_date, 'MMM D, YYYY')}</span>
    {ev.location && <Place location={ev.location} className="hidden min-w-0 truncate text-xs text-on-surface-variant md:inline" />}
    <Tooltip label="Stop taking dates from this event">
      <Button variant="quiet" size="sm" className="ml-auto flex-none" onClick={onUnlink}>Unlink</Button>
    </Tooltip>
  </li>
);

/**
 * The linked event, in the project header: the record's most important relationship. Its name
 * opens the event; the date counts down and the plate glows in the week the show flies. Linking,
 * unlinking, the search and the move-the-dates prompt all happen here, in place.
 */
export const SparkEventPlate: React.FC<{
  productionId: string;
  events: LinkedEvent[];
  isStanding: boolean;
  studio: boolean;
  offer: DateOffer | null;
  onOfferChange: (offer: DateOffer | null) => void;
  onChanged: () => void;
}> = ({ productionId, events, isStanding, studio, offer, onOfferChange, onChanged }) => {
  const toast = useToast();
  const confirm = useConfirm();
  const [picking, setPicking] = useState(false);
  const [showMore, setShowMore] = useState(false);
  const [busy, setBusy] = useState(false);
  const pickButton = useRef<HTMLButtonElement>(null);
  const wasPicking = useRef(false);

  // However the search closes (a pick, Cancel, Escape), focus goes back to the key that opened it.
  useEffect(() => {
    if (wasPicking.current && !picking) pickButton.current?.focus();
    wasPicking.current = picking;
  }, [picking]);

  if (isStanding) return null;

  const link = async (hit: EventHit) => {
    if (busy) return;
    setBusy(true);
    try {
      const r = await linkEvent(productionId, hit.id);
      setPicking(false);
      onOfferChange(r.offer || null);
      toast.success(`Linked ${hit.name}.`);
      onChanged();
    } catch (e) {
      toast.error(errorMessage(e, 'Could not link that event. Try again.'));
    }
    setBusy(false);
  };

  const unlink = async (ev: LinkedEvent) => {
    const ok = await confirm({
      title: 'Unlink this event?',
      message: `${ev.name} stops feeding this project its show date and drone counts. The dates stay as they are.`,
      confirmLabel: 'Unlink event',
      tone: 'danger',
    });
    if (!ok) return;
    try {
      await unlinkEvent(productionId, ev.id);
      onOfferChange(null);
      onChanged();
    } catch (e) {
      toast.error(errorMessage(e, 'Could not unlink that event. Try again.'));
    }
  };

  const lead = events[0];
  const more = events.slice(1);
  const when = lead ? showWhen(lead.event_date) : null;
  const pickLabel = picking ? 'Cancel' : lead ? 'Link another' : 'Link an event';

  return (
    <section aria-label="Linked event" className="mt-4">
      <div
        className={`grid min-w-0 grid-cols-[auto_minmax(0,1fr)] items-center gap-x-4 gap-y-2 rounded-panel bg-surface-container-low px-4 py-3 transition-[box-shadow] duration-standard ease-emphasized sm:grid-cols-[auto_minmax(0,1fr)_auto] ${
          when?.soon ? 'shadow-glow' : ''
        }`}
      >
        <IconSpark className={`h-6 w-6 flex-none ${lead ? 'text-primary drop-shadow-glow' : 'text-on-surface-disabled'}`} />
        {lead && when ? (
          <div key={lead.id} className="min-w-0 motion-safe:animate-rowLand">
            <span className="block text-xs text-on-surface-variant">{more.length ? `Linked events, ${events.length}` : 'Linked event'}</span>
            <Link
              to={`/events/${lead.id}`}
              className={`group inline-flex max-w-full items-center gap-1 rounded-inner text-base font-semibold leading-6 text-on-surface ${FOCUS}`}
            >
              <span className="min-w-0 underline-offset-2 [overflow-wrap:anywhere] group-hover:underline sm:truncate">{lead.name}</span>
              <IconArrowUpRight className="h-4 w-4 flex-none text-on-surface-variant transition-transform duration-quick ease-emphasized group-hover:-translate-y-px group-hover:translate-x-px" />
            </Link>
            <span className="flex min-w-0 whitespace-pre text-sm tabular-nums text-on-surface-variant">
              <span className="flex-none text-on-surface">{dayText(lead.event_date, 'MMM D, YYYY')}</span>
              <span className="flex-none">, </span>
              <span className={`flex-none ${when.soon ? 'font-medium text-primary' : ''}`}>{when.text}</span>
              {lead.location && <span className="flex-none">, </span>}
              {lead.location && <Place location={lead.location} className="min-w-0 truncate" />}
            </span>
          </div>
        ) : (
          <EmptyState variant="line" className="motion-safe:animate-rowLand">
            No event linked. This project runs on its own show date until one is.
          </EmptyState>
        )}
        {/* Under the name on a phone, so the name keeps the full width; beside it from sm. */}
        <div className={`col-start-2 flex min-w-0 flex-wrap items-center gap-1.5 sm:col-start-auto sm:ml-0 sm:justify-end ${lead ? '-ml-2.5' : ''}`}>
          <Appear show={more.length > 0} className="inline-flex">
            <Tooltip label={showMore ? 'Hide the other events' : 'Show every linked event'}>
              <Button variant="quiet" size="sm" aria-expanded={showMore} onClick={() => setShowMore(!showMore)}>
                {showMore ? 'Fewer' : `+${more.length} more`}
              </Button>
            </Tooltip>
          </Appear>
          <Tooltip label={picking ? 'Close the event search' : 'Search for an event to link'}>
            <Button ref={pickButton} variant={lead ? 'quiet' : 'tonal'} size="sm" aria-expanded={picking} leading={picking ? undefined : <IconPlus className="h-3.5 w-3.5" />} onClick={() => setPicking(!picking)}>
              {pickLabel}
            </Button>
          </Tooltip>
          <Appear show={!!lead} className="inline-flex">
            <Tooltip label="Stop taking dates from this event">
              <Button variant="quiet" size="sm" onClick={() => { if (lead) unlink(lead); }}>Unlink</Button>
            </Tooltip>
          </Appear>
        </div>
      </div>

      <Reveal show={showMore && more.length > 0}>
        <ul className="m-0 mt-2 list-none space-y-1 p-0">
          {more.map((ev) => <MoreEventRow key={ev.id} ev={ev} onUnlink={() => unlink(ev)} />)}
        </ul>
      </Reveal>

      <Reveal show={picking}>
        <div className="mt-2 rounded-panel bg-surface-container-low p-3">
          <span className="text-[13px] font-medium text-on-surface">Pick the event</span>
          <EventLinkPicker autoFocus onPick={link} onCancel={() => setPicking(false)} />
        </div>
      </Reveal>

      <Reveal show={!!offer}>
        {offer && <DateOfferPrompt productionId={productionId} offer={offer} studio={studio} onDone={(shifted) => { onOfferChange(null); if (shifted) onChanged(); }} />}
      </Reveal>
    </section>
  );
};
